import React, { useCallback, useEffect, useState } from 'react';
import { companyCommentService } from '../../services/apiService';
import BillingManagement from '../BillingManagement';
import InvoiceActions from '../InvoiceActions';
import CompanyComments from '../CompanyComments';
import { serverMessage } from '../PageState';
import { buildBillingChangeNote } from '../../utils/billingNotes';
import { toDateInputValue } from './companyFormat';
import { useCompanyForm } from './companyHooks';

/** The billing form's own fields, as its inputs expect them. */
const toBillingDraft = (company) => ({
  accountType: company.accountType || '',
  paymentFrequency: company.paymentFrequency || '',
  renewalDate: toDateInputValue(company.renewalDate),
  preferredPointOfContact: company.preferredPointOfContact || '',
  freeUsers: company.freeUsers || 0,
  paidUsers: company.paidUsers || 0,
  overduePayment: !!company.overduePayment,
  usesStripe: !!company.usesStripe,
});

/**
 * The Billing section: plan, seats, renewal and payment flags (saved on their own, after a
 * review of what changes), then the account's comments with invoice logging in their header.
 */
const CompanyBilling = ({ companyId, company, canEdit, api, onSaveCompany, onDirtyChange }) => {
  const form = useCompanyForm({
    formKey: 'billing',
    company,
    toDraft: toBillingDraft,
    onSaveCompany,
    onDirtyChange,
  });

  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentError, setCommentError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      setCommentError(null);
      const response = await api(() => companyCommentService.getComments(companyId));
      setComments(response.data);
    } catch (err) {
      setCommentError(serverMessage(err, 'Could not load the comments. Try again.'));
      console.error(err);
    } finally {
      setCommentsLoading(false);
    }
  }, [api, companyId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSave = async () => {
    // Worked out before saving, while the company still holds the old values
    const note = buildBillingChangeNote(company, form.draft);
    const saved = await form.save();
    if (!saved || !note) return;
    try {
      await api(() => companyCommentService.createComment(companyId, note));
      fetchComments();
    } catch (err) {
      console.error('Failed to record billing change note', err);
      form.setStatus({ type: 'warning', message: 'Saved, but the change note could not be added to the comments.' });
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      setIsPostingComment(true);
      setCommentError(null);
      const response = await api(() => companyCommentService.createComment(companyId, newComment.trim()));
      setComments((prev) => [...prev, response.data]);
      setNewComment('');
    } catch (err) {
      setCommentError(serverMessage(err, err.message || 'Could not add the comment'));
      console.error(err);
    } finally {
      setIsPostingComment(false);
    }
  };

  const handleAddInvoiceComment = async (commentText) => {
    await api(() => companyCommentService.createComment(companyId, commentText));
    fetchComments();
  };

  return (
    <>
      <div id="billing-form" style={{ scrollMarginTop: 120 }}>
        <BillingManagement
          values={form.draft}
          savedValues={company}
          onFieldChange={form.setField}
          onSave={handleSave}
          isSaving={form.saving}
          saveStatus={form.status}
          canEdit={canEdit}
        />
      </div>
      <CompanyComments
        comments={comments}
        commentsLoading={commentsLoading}
        commentError={commentError}
        onDismissError={() => setCommentError(null)}
        newComment={newComment}
        onNewCommentChange={setNewComment}
        onAddComment={handleAddComment}
        isPostingComment={isPostingComment}
        canEdit={canEdit}
        headerActions={canEdit ? <InvoiceActions compact canEdit={canEdit} onAddComment={handleAddInvoiceComment} /> : null}
      />
    </>
  );
};

export default CompanyBilling;
